import {useMemo} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {
  ALL_STATES_FILTER,
  PHASE,
  Phase,
} from '@/config/contentSubmissionPhases';
import type {
  ContentSubmission,
  ContentSubmissionKind,
  ContentSubmissionState,
} from '@/types/contentSubmission';
import {ContentSubmissionSort, SortDir} from '@/types/sorting';
import {ContentSubmissionsMetaItem} from '@/api/fetchers/campaignContentSubmission';
import useCampaignContentSubmissionsMetaQuery from '@/hooks/queries/use-campaign-content-submissions-meta-query';
import useContentSubmissionQuery from '@/hooks/queries/use-content-submission-query';

function useContentSubmissionsMeta() {
  const {campaignId, contentSubmissionId, parentContentSubmissionId} =
    useParams<{
      campaignId: string;
      contentSubmissionId: string;
      parentContentSubmissionId: string;
    }>();
  const [searchParams] = useSearchParams();

  const isStoryView = !!parentContentSubmissionId && !contentSubmissionId;

  // Read filters from URL search params (not Zustand store) because the store
  // is cleared when navigating away from the list tab, but URL params survive.
  const phase = (searchParams.get('phase') ?? PHASE.ALL) as Phase;
  const isOnAllTab = phase === PHASE.ALL;
  const statusParam = searchParams.get('status') ?? undefined;
  const kindParam = searchParams.get('kind') ?? undefined;
  const profileIdParam = searchParams.get('profile_id') ?? undefined;
  const sortBy = (searchParams.get('sortBy') ?? undefined) as
    | ContentSubmissionSort
    | undefined;
  const sortDir = (searchParams.get('sortDir') ?? undefined) as
    | SortDir
    | undefined;

  const getStateFilter = () => {
    if (isOnAllTab && statusParam) {
      return statusParam;
    }
    if (isOnAllTab) {
      return ALL_STATES_FILTER;
    }
    return phase as ContentSubmissionState;
  };
  const stateFilter = getStateFilter();

  // Filters only apply on the All tab; non-all tabs show their full phase.
  const selectedKind = isOnAllTab
    ? (kindParam as ContentSubmissionKind | undefined)
    : undefined;
  const profileId = isOnAllTab ? profileIdParam : undefined;

  const {data: contentSubmissionsMetaData} =
    useCampaignContentSubmissionsMetaQuery({
      campaignId: campaignId ?? '',
      state: stateFilter,
      kind: selectedKind,
      profileId,
      enabled: !!campaignId,
      staleTime: Infinity,
      sortBy,
      sortDir,
    });

  const {data: storyData} = useContentSubmissionQuery({
    contentSubmissionId: parentContentSubmissionId,
    enabled:
      !!contentSubmissionId && !!parentContentSubmissionId && !isStoryView,
  });

  const story = storyData as ContentSubmission | undefined;

  const dataSource: ContentSubmissionsMetaItem[] = useMemo(() => {
    if (isStoryView) {
      return contentSubmissionsMetaData ?? [];
    }
    return (
      (parentContentSubmissionId
        ? (story?.childContentSubmissions as
            | ContentSubmissionsMetaItem[]
            | undefined)
        : contentSubmissionsMetaData) ?? []
    );
  }, [
    isStoryView,
    contentSubmissionsMetaData,
    parentContentSubmissionId,
    story?.childContentSubmissions,
  ]);

  // In story view, we navigate between top-level items using parentContentSubmissionId.
  // In content submission view, we navigate using contentSubmissionId.
  const activeId = isStoryView
    ? parentContentSubmissionId
    : contentSubmissionId;
  const current = dataSource.findIndex((item) => item.id === activeId) + 1;

  const total = dataSource.length;

  return {dataSource, current, total};
}

export default useContentSubmissionsMeta;
